/**
 * RunOrchestrationContext - Centralized state management for run orchestration
 * 
 * This context provides all the shared state needed for run lifecycle management
 * across the application. It consolidates the 50+ useState calls from AppContent.tsx
 * into a single, type-safe context.
 */
import { createContext, useContext, useState, useRef, ReactNode } from 'react';
import type { Screen } from '../routes';
import type { Run } from '../data/runs/runs';
import type { PointsLoop } from '../utils/api/pointsLoops';
import type { HashRun } from '../data/runs/hashRuns';
import type { EnteredPublicEvent } from '../data/events/enteredPublicEvents';
import type { EventSummary } from '../types/events';
import type {
  CurrentEventData,
  PointsLoopStateData,
  SelectedCheckpointData,
  LocationCoord,
  ClassicLoopBrowseState,
  PointsLoopBrowseState,
  EventLoopBrowseState,
  EventPointsLoopBrowseState,
  EventHashRunBrowseState,
  PublicEventHashRunBrowseState,
  ClassicRunResumeData,
  RunDetailData,
  CheckpointSplitData,
  EventsTabState,
  PendingPublicEventData,
  PendingPublicHashEventData,
} from './types';

// Re-export types for consumers
export type {
  CurrentEventData,
  PointsLoopStateData,
  SelectedCheckpointData,
  LocationCoord,
  ClassicLoopBrowseState,
  PointsLoopBrowseState,
  EventLoopBrowseState,
  EventPointsLoopBrowseState,
  EventHashRunBrowseState,
  PublicEventHashRunBrowseState,
  ClassicRunResumeData,
  RunDetailData,
  CheckpointSplitData,
  EventsTabState,
  PendingPublicEventData,
  PendingPublicHashEventData,
};

// ============================================================================
// Context State Interface
// ============================================================================

export interface RunOrchestrationState {
  // Navigation
  navigationStack: Screen[];
  setNavigationStack: React.Dispatch<React.SetStateAction<Screen[]>>;
  shouldResetMyLoopsHistory: boolean;
  setShouldResetMyLoopsHistory: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Active runs
  activeRunId: string | null;
  setActiveRunId: React.Dispatch<React.SetStateAction<string | null>>;
  activePointsRunId: string | null;
  setActivePointsRunId: React.Dispatch<React.SetStateAction<string | null>>;
  
  // Events
  currentEvent: CurrentEventData | null;
  setCurrentEvent: React.Dispatch<React.SetStateAction<CurrentEventData | null>>;
  currentPublicEvent: EnteredPublicEvent | null;
  setCurrentPublicEvent: React.Dispatch<React.SetStateAction<EnteredPublicEvent | null>>;
  activeEventForRun: EventSummary | null;
  setActiveEventForRun: React.Dispatch<React.SetStateAction<EventSummary | null>>;
  createdEvents: EventSummary[];
  setCreatedEvents: React.Dispatch<React.SetStateAction<EventSummary[]>>;
  enteredPublicEvents: EnteredPublicEvent[];
  setEnteredPublicEvents: React.Dispatch<React.SetStateAction<EnteredPublicEvent[]>>;
  
  // Selection state
  howToLoopMode: 'info' | 'preRun';
  setHowToLoopMode: React.Dispatch<React.SetStateAction<'info' | 'preRun'>>;
  selectedRun: Run | null;
  setSelectedRun: React.Dispatch<React.SetStateAction<Run | null>>;
  selectedLoop: any | null;
  setSelectedLoop: React.Dispatch<React.SetStateAction<any | null>>;
  selectedPointsLoop: PointsLoop | null;
  setSelectedPointsLoop: React.Dispatch<React.SetStateAction<PointsLoop | null>>;
  selectedHashRun: HashRun | null;
  setSelectedHashRun: React.Dispatch<React.SetStateAction<HashRun | null>>;
  selectedCheckpoint: SelectedCheckpointData | null;
  setSelectedCheckpoint: React.Dispatch<React.SetStateAction<SelectedCheckpointData | null>>;
  
  // Points loop state
  pointsLoopMode: 'select' | 'navigate' | 'running';
  setPointsLoopMode: React.Dispatch<React.SetStateAction<'select' | 'navigate' | 'running'>>;
  pointsLoopState: PointsLoopStateData;
  setPointsLoopState: React.Dispatch<React.SetStateAction<PointsLoopStateData>>;
  pointsLoopTimerActive: boolean;
  setPointsLoopTimerActive: React.Dispatch<React.SetStateAction<boolean>>;
  pointsLoopLegStartOverride: LocationCoord | null;
  setPointsLoopLegStartOverride: React.Dispatch<React.SetStateAction<LocationCoord | null>>;
  pointsLoopHasReturnedToStart: boolean;
  setPointsLoopHasReturnedToStart: React.Dispatch<React.SetStateAction<boolean>>;
  visitedCheckpoints: string[];
  setVisitedCheckpoints: React.Dispatch<React.SetStateAction<string[]>>;
  completedPointsRunId: string | null;
  setCompletedPointsRunId: React.Dispatch<React.SetStateAction<string | null>>;
  completedPointsRunElapsedTime: number;
  setCompletedPointsRunElapsedTime: React.Dispatch<React.SetStateAction<number>>;
  isNavigatingToStart: boolean;
  setIsNavigatingToStart: React.Dispatch<React.SetStateAction<boolean>>;
  
  // UI state
  showPointsLoopSummaryModal: boolean;
  setShowPointsLoopSummaryModal: React.Dispatch<React.SetStateAction<boolean>>;
  showEnterCodeModal: boolean;
  setShowEnterCodeModal: React.Dispatch<React.SetStateAction<boolean>>;
  showEnterPublicEventModal: boolean;
  setShowEnterPublicEventModal: React.Dispatch<React.SetStateAction<boolean>>;
  showingGlobalLeaderboard: boolean;
  setShowingGlobalLeaderboard: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Settings
  manualMovementEnabled: boolean;
  setManualMovementEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  showUserLocationOnMaps: boolean;
  setShowUserLocationOnMaps: React.Dispatch<React.SetStateAction<boolean>>;
  hasCheckedForActiveRun: boolean;
  setHasCheckedForActiveRun: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Browse state caches
  classicLoopState: ClassicLoopBrowseState;
  setClassicLoopState: React.Dispatch<React.SetStateAction<ClassicLoopBrowseState>>;
  pointsLoopState2: PointsLoopBrowseState;
  setPointsLoopState2: React.Dispatch<React.SetStateAction<PointsLoopBrowseState>>;
  eventLoopBrowseState: EventLoopBrowseState;
  setEventLoopBrowseState: React.Dispatch<React.SetStateAction<EventLoopBrowseState>>;
  eventPointsLoopBrowseState: EventPointsLoopBrowseState;
  setEventPointsLoopBrowseState: React.Dispatch<React.SetStateAction<EventPointsLoopBrowseState>>;
  eventHashRunBrowseState: EventHashRunBrowseState;
  setEventHashRunBrowseState: React.Dispatch<React.SetStateAction<EventHashRunBrowseState>>;
  publicEventHashRunBrowseState: PublicEventHashRunBrowseState;
  setPublicEventHashRunBrowseState: React.Dispatch<React.SetStateAction<PublicEventHashRunBrowseState>>;
  
  // Events tab state
  eventsTabState: EventsTabState;
  setEventsTabState: React.Dispatch<React.SetStateAction<EventsTabState>>;
  eventsScrollPosition: number;
  setEventsScrollPosition: React.Dispatch<React.SetStateAction<number>>;
  
  // Resume data
  classicRunResumeData: ClassicRunResumeData | null;
  setClassicRunResumeData: React.Dispatch<React.SetStateAction<ClassicRunResumeData | null>>;
  activeRunInitialCamera: any | null;
  setActiveRunInitialCamera: React.Dispatch<React.SetStateAction<any | null>>;
  classicRunResumeCameraKey: number;
  setClassicRunResumeCameraKey: React.Dispatch<React.SetStateAction<number>>;
  
  // Run detail data
  runDetailData: RunDetailData | null;
  setRunDetailData: React.Dispatch<React.SetStateAction<RunDetailData | null>>;
  checkpointSplitsData: CheckpointSplitData[];
  setCheckpointSplitsData: React.Dispatch<React.SetStateAction<CheckpointSplitData[]>>;
  
  // Refresh trigger
  refreshCounter: number;
  setRefreshCounter: React.Dispatch<React.SetStateAction<number>>;
  
  // Pending public event
  pendingPublicEvent: PendingPublicEventData | null;
  setPendingPublicEvent: React.Dispatch<React.SetStateAction<PendingPublicEventData | null>>;
  pendingPublicHashEvent: PendingPublicHashEventData | null;
  setPendingPublicHashEvent: React.Dispatch<React.SetStateAction<PendingPublicHashEventData | null>>;
  
  // Refs
  prefetchingLoopKeyRef: React.MutableRefObject<string | null>;
  pendingClassicRunStartRef: React.MutableRefObject<string | null>;
  previousScreenRef: React.MutableRefObject<Screen | null>;
}

// Context Creation
const RunOrchestrationContext = createContext<RunOrchestrationState | null>(null);

// Provider Component
interface RunOrchestrationProviderProps {
  children: ReactNode;
}

export function RunOrchestrationProvider({ children }: RunOrchestrationProviderProps) {
  // Navigation
  const [navigationStack, setNavigationStack] = useState<Screen[]>([]);
  const [shouldResetMyLoopsHistory, setShouldResetMyLoopsHistory] = useState(false);
  
  // Active runs
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activePointsRunId, setActivePointsRunId] = useState<string | null>(null);
  
  // Events
  const [currentEvent, setCurrentEvent] = useState<CurrentEventData | null>(null);
  const [currentPublicEvent, setCurrentPublicEvent] = useState<EnteredPublicEvent | null>(null);
  const [activeEventForRun, setActiveEventForRun] = useState<EventSummary | null>(null);
  const [createdEvents, setCreatedEvents] = useState<EventSummary[]>([]);
  const [enteredPublicEvents, setEnteredPublicEvents] = useState<EnteredPublicEvent[]>([]);
  
  // Selection state
  const [howToLoopMode, setHowToLoopMode] = useState<'info' | 'preRun'>('info');
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);
  const [selectedLoop, setSelectedLoop] = useState<any | null>(null);
  const [selectedPointsLoop, setSelectedPointsLoop] = useState<PointsLoop | null>(null);
  const [selectedHashRun, setSelectedHashRun] = useState<HashRun | null>(null);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<SelectedCheckpointData | null>(null);
  
  // Points loop state
  const [pointsLoopMode, setPointsLoopMode] = useState<'select' | 'navigate' | 'running'>('select');
  const [pointsLoopState, setPointsLoopState] = useState<PointsLoopStateData>({ 
    currentPoints: 0, timeLeft: 0, totalAccuracyPenalties: 0, totalDistance: 0 
  });
  const [pointsLoopTimerActive, setPointsLoopTimerActive] = useState(false);
  const [pointsLoopLegStartOverride, setPointsLoopLegStartOverride] = useState<LocationCoord | null>(null);
  const [pointsLoopHasReturnedToStart, setPointsLoopHasReturnedToStart] = useState(false);
  const [visitedCheckpoints, setVisitedCheckpoints] = useState<string[]>([]);
  const [completedPointsRunId, setCompletedPointsRunId] = useState<string | null>(null);
  const [completedPointsRunElapsedTime, setCompletedPointsRunElapsedTime] = useState<number>(0);
  const [isNavigatingToStart, setIsNavigatingToStart] = useState(false);
  
  // UI state
  const [showPointsLoopSummaryModal, setShowPointsLoopSummaryModal] = useState(false);
  const [showEnterCodeModal, setShowEnterCodeModal] = useState(false);
  const [showEnterPublicEventModal, setShowEnterPublicEventModal] = useState(false);
  const [showingGlobalLeaderboard, setShowingGlobalLeaderboard] = useState(false);
  
  // Settings
  const [manualMovementEnabled, setManualMovementEnabled] = useState(false);
  const [showUserLocationOnMaps, setShowUserLocationOnMaps] = useState(false);
  const [hasCheckedForActiveRun, setHasCheckedForActiveRun] = useState(false);
  
  // Browse state caches
  const [classicLoopState, setClassicLoopState] = useState<ClassicLoopBrowseState>({
    selectedLocationId: null, currentRunIndex: 0, loops: [], mapCenter: null, zoom: null,
    selectedLengths: [], selectedElevations: [], selectedComplexity: [], showCompletedLoops: false,
  });
  const [pointsLoopState2, setPointsLoopState2] = useState<PointsLoopBrowseState>({
    selectedLocationId: null, currentLoopIndex: 0,
  });
  const [eventLoopBrowseState, setEventLoopBrowseState] = useState<EventLoopBrowseState>({
    selectedLocationId: null, currentRunIndex: 0, loops: [], mapCenter: null, zoom: null,
    selectedLengths: [], selectedElevations: [], selectedComplexity: [],
  });
  const [eventPointsLoopBrowseState, setEventPointsLoopBrowseState] = useState<EventPointsLoopBrowseState>({
    selectedLocationId: null, currentLoopIndex: 0,
  });
  const [eventHashRunBrowseState, setEventHashRunBrowseState] = useState<EventHashRunBrowseState>({
    selectedLocationId: null, currentRunIndex: 0,
  });
  const [publicEventHashRunBrowseState, setPublicEventHashRunBrowseState] = useState<PublicEventHashRunBrowseState>({
    selectedLocationId: null, currentRunIndex: 0,
  });
  
  // Events tab state
  const [eventsTabState, setEventsTabState] = useState<EventsTabState>('upcoming');
  const [eventsScrollPosition, setEventsScrollPosition] = useState(0);
  
  // Resume data
  const [classicRunResumeData, setClassicRunResumeData] = useState<ClassicRunResumeData | null>(null);
  const [activeRunInitialCamera, setActiveRunInitialCamera] = useState<any | null>(null);
  const [classicRunResumeCameraKey, setClassicRunResumeCameraKey] = useState(0);
  
  // Run detail data
  const [runDetailData, setRunDetailData] = useState<RunDetailData | null>(null);
  const [checkpointSplitsData, setCheckpointSplitsData] = useState<CheckpointSplitData[]>([]);
  
  // Refresh trigger
  const [refreshCounter, setRefreshCounter] = useState(0);
  
  // Pending public event
  const [pendingPublicEvent, setPendingPublicEvent] = useState<PendingPublicEventData | null>(null);
  const [pendingPublicHashEvent, setPendingPublicHashEvent] = useState<PendingPublicHashEventData | null>(null);
  
  // Refs
  const prefetchingLoopKeyRef = useRef<string | null>(null);
  const pendingClassicRunStartRef = useRef<string | null>(null);
  const previousScreenRef = useRef<Screen | null>(null);
  
  const value: RunOrchestrationState = {
    navigationStack, setNavigationStack, shouldResetMyLoopsHistory, setShouldResetMyLoopsHistory,
    activeRunId, setActiveRunId, activePointsRunId, setActivePointsRunId,
    currentEvent, setCurrentEvent, currentPublicEvent, setCurrentPublicEvent,
    activeEventForRun, setActiveEventForRun, createdEvents, setCreatedEvents,
    enteredPublicEvents, setEnteredPublicEvents, howToLoopMode, setHowToLoopMode,
    selectedRun, setSelectedRun, selectedLoop, setSelectedLoop,
    selectedPointsLoop, setSelectedPointsLoop, selectedHashRun, setSelectedHashRun,
    selectedCheckpoint, setSelectedCheckpoint, pointsLoopMode, setPointsLoopMode,
    pointsLoopState, setPointsLoopState, pointsLoopTimerActive, setPointsLoopTimerActive,
    pointsLoopLegStartOverride, setPointsLoopLegStartOverride,
    pointsLoopHasReturnedToStart, setPointsLoopHasReturnedToStart,
    visitedCheckpoints, setVisitedCheckpoints, completedPointsRunId, setCompletedPointsRunId,
    completedPointsRunElapsedTime, setCompletedPointsRunElapsedTime,
    isNavigatingToStart, setIsNavigatingToStart,
    showPointsLoopSummaryModal, setShowPointsLoopSummaryModal,
    showEnterCodeModal, setShowEnterCodeModal, showEnterPublicEventModal, setShowEnterPublicEventModal,
    showingGlobalLeaderboard, setShowingGlobalLeaderboard,
    manualMovementEnabled, setManualMovementEnabled, showUserLocationOnMaps, setShowUserLocationOnMaps,
    hasCheckedForActiveRun, setHasCheckedForActiveRun,
    classicLoopState, setClassicLoopState, pointsLoopState2, setPointsLoopState2,
    eventLoopBrowseState, setEventLoopBrowseState,
    eventPointsLoopBrowseState, setEventPointsLoopBrowseState,
    eventHashRunBrowseState, setEventHashRunBrowseState,
    publicEventHashRunBrowseState, setPublicEventHashRunBrowseState,
    eventsTabState, setEventsTabState, eventsScrollPosition, setEventsScrollPosition,
    classicRunResumeData, setClassicRunResumeData, activeRunInitialCamera, setActiveRunInitialCamera,
    classicRunResumeCameraKey, setClassicRunResumeCameraKey,
    runDetailData, setRunDetailData, checkpointSplitsData, setCheckpointSplitsData,
    refreshCounter, setRefreshCounter,
    pendingPublicEvent, setPendingPublicEvent, pendingPublicHashEvent, setPendingPublicHashEvent,
    prefetchingLoopKeyRef, pendingClassicRunStartRef, previousScreenRef,
  };
  
  return (
    <RunOrchestrationContext.Provider value={value}>
      {children}
    </RunOrchestrationContext.Provider>
  );
}

// Hook
export function useRunOrchestration(): RunOrchestrationState {
  const context = useContext(RunOrchestrationContext);
  if (!context) {
    throw new Error('useRunOrchestration must be used within a RunOrchestrationProvider');
  }
  return context;
}

export default RunOrchestrationContext;
